import {
    AsOfBadge,
    DateTime,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { Subscription } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/subscriptions';
const PAGE_KEY = 'subscriptions';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const subscriptionsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

// bm-147 § 5: the six filters of BM-147 #4
const subscriptionFilters = filterDefs(PATH, {
    status: 'Status',
    merchant_id: 'Merchant',
    store_id: 'Store',
    buyer_email: 'Buyer email',
    price_public_id: 'Price',
    range: 'Created',
});

/** The subscription page (bm-147 § 5). */
export const subscriptionHref = (id: string) =>
    `/subscriptions/${encodeURIComponent(id)}`;

/** "$29.00 every 1 month": the amount and the price's recurring interval (BM-117 § 14), as the list and the page show them. */
export function AmountEvery({ s }: { s: Subscription }) {
    return (
        <>
            <Money cents={s.amount_cents} currency={s.currency} />
            {s.recurring?.interval
                ? ` every ${s.recurring.interval_count ?? 1} ${s.recurring.interval}`
                : null}
        </>
    );
}

/**
 * bm-147 § 5 (US-005): every merchant's subscriptions, newest first (the list contract's default), with the ticket's
 * columns on the shared subscription shape.
 */
export function SubscriptionsPage() {
    const columns: ListColumnDef<Subscription>[] = [
        {
            key: 'id',
            label: 'Subscription',
            cell: (r) => (
                <Link
                    to={subscriptionHref(r.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {r.id}
                </Link>
            ),
            csv: (r) => r.id,
            boardCard: true,
            noTruncate: true,
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            cell: (r) => <ObjectRef id={r.merchant_id} />,
        },
        {
            key: 'store',
            label: 'Store',
            csv: (r) => r.store_id ?? '',
            cell: (r) =>
                r.store ? (
                    <ObjectRef
                        id={r.store.id}
                        label={r.store.display_name ?? r.store.name}
                        href={`/stores/${encodeURIComponent(r.store.id)}`}
                    />
                ) : (
                    '—'
                ),
        },
        {
            key: 'buyer_email',
            aliases: ['buyer'],
            label: 'Buyer',
            sortKey: 'buyer_email',
            csv: (r) => r.buyer_email,
            cell: (r) => r.buyer_email,
            boardCard: true,
        },
        {
            key: 'price_id',
            // a saved view's former plan column reconciles to the price column (BM-117 § 14)
            aliases: ['price', 'plan_id', 'plan'],
            label: 'Price',
            csv: (r) => r.price_id,
            cell: (r) => <PublicId id={r.price_id} />,
        },
        {
            key: 'amount_cents',
            aliases: ['amount'],
            label: 'Amount',
            sortKey: 'amount_cents',
            csv: (r) => r.amount_cents,
            cell: (r) => <AmountEvery s={r} />,
            noTruncate: true,
        },
        {
            key: 'status',
            label: 'Status',
            sortKey: 'status',
            csv: (r) => r.status,
            boardCard: true,
            cell: (r) => (
                <StatusBadge status={r.status} domain="subscription" />
            ),
            groupable: { key: 'status', statusDomain: 'subscription' },
        },
        {
            key: 'current_period_end',
            label: 'Current period end',
            kind: 'datetime',
            sortKey: 'current_period_end',
            csv: (r) => r.current_period_end,
            cell: (r) => <DateTime value={r.current_period_end} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'next_charge_at',
            aliases: ['next_renewal'],
            label: 'Next renewal',
            kind: 'datetime',
            sortKey: 'next_charge_at',
            csv: (r) => r.next_charge_at ?? '',
            cell: (r) => <DateTime value={r.next_charge_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'payment_method_id',
            aliases: ['payment_method'],
            label: 'Payment method',
            csv: (r) => r.payment_method_id ?? '',
            cell: (r) =>
                r.payment_method_id ? (
                    <ObjectRef id={r.payment_method_id} />
                ) : (
                    '—'
                ),
        },
        {
            key: 'created_at',
            label: 'Created',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (r) => r.created_at ?? '',
            cell: (r) => <DateTime value={r.created_at} empty="—" />,
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<Subscription>(PAGE_KEY, PATH, {
        columns,
        filters: subscriptionFilters,
    });
    return (
        <div>
            <PageHeader
                title="Subscriptions"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Subscriptions across merchants, with their price,
                            status and next renewal.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="subscriptions"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                rowHref={(r) => subscriptionHref(r.id)}
                controls={{ status: { domain: 'subscription' } }}
                mobileCard={{
                    fields: ['status', 'buyer_email'],
                }}
            />
        </div>
    );
}
